import { Component, OnDestroy, OnInit, ViewChild, isDevMode } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import {combineLatest, Observable, of, Subject} from 'rxjs';
import firebase from 'firebase/compat/app';
import { LoadingService } from '../../../../../services/loading.service';
import { PermissionType } from '../../../../../components/employee/PermissionType.enum';
import { CentreService } from '../../../../../services/centre.service';
import {map, takeUntil} from "rxjs/operators";
import { FinancialYearService } from '../../../../../services/financial-year.service';
import { environment } from '../../../../../../environments/environment';

interface PresentationData {
  id?: string;
  selectedCentre: string;
  centreName: string;
  startDate: firebase.firestore.Timestamp;
  endDate: firebase.firestore.Timestamp;
  componentVisibility: any[];
  sessionsBehavioral: string;
  sessionsEmotional: string;
  sessionsOther: string;
  remarks: string;
  createdAt: firebase.firestore.Timestamp;
  createdBy: string;
  lastModified: firebase.firestore.Timestamp;
}

@Component({
  selector: 'app-counselling-presentation-list',
  templateUrl: './counselling-presentation-list.component.html',
  styleUrls: ['./counselling-presentation-list.component.scss']
})
export class CounsellingPresentationListComponent implements OnInit, OnDestroy {
  env = isDevMode() ? environment.testMode : environment.productionMode;
  private _destroyed$ = new Subject<void>();
  displayedColumns: string[] = [
    'no',
    'centreName',
    'dateRange',
    'createdBy',
    'createdAt',
    'actions'
  ];

  dataSource = new MatTableDataSource<PresentationData>([]);
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  presentations$: Observable<PresentationData[]>;
  searchText: string = '';

  // Filters
  start: Date | null = null;
  end: Date | null = null;
  filterCentre: any[] = [];
  centres$: Observable<any[]>;

  // Expose PermissionType to template
  readonly PermissionType = PermissionType;

  constructor(
    private firestore: AngularFirestore,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private router: Router,
    public loadingService: LoadingService,
    private centreService: CentreService,
    private financialYearService: FinancialYearService
  ) {
    this.presentations$ = this.firestore.collection<PresentationData>(this.env.counsellingPresentationList,
      ref => ref.orderBy('createdAt', 'desc')
    ).valueChanges({ idField: 'id' });

    this.centres$ = this.centreService.getAllCentres().pipe(
      map(centres => [...centres].sort((a, b) => (a.centreName ?? '').trim().localeCompare((b.centreName ?? '').trim())))
    );
  }

  ngOnInit(): void {
    const fy = this.financialYearService.getCurrentFinancialYear();
    this.start = fy.startDate;
    this.end = fy.endDate;
    this.loadPresentations();
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  loadPresentations(): void {
    this.loadingService.setLoading(true);
    this.presentations$.pipe(takeUntil(this._destroyed$)).subscribe(
      (data) => {
        this.dataSource.data = data;
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;
        this.loadingService.setLoading(false);
      },
      (error) => {
        console.error('Error loading presentations:', error);
        this.snackBar.open('Error loading presentations', 'Close', { duration: 3000 });
        this.loadingService.setLoading(false);
      }
    );
  }

  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  onCentreSelection(selected: any[]) {
    this.filterCentre = selected;
  }
  applyAdvancedFilter(): void {

    this.loadingService.setLoading(true);

    const centreCodes = this.filterCentre.map(c => c.centreCode);
    // Firestore "in" supports a maximum of 10 values; for larger selections we
    // skip the server-side centre constraint and filter client-side below.
    const useCentreInQuery = centreCodes.length > 0 && centreCodes.length <= 10;
    const centreSet = new Set(centreCodes);

    let finalQuery$ = this.firestore.collection<PresentationData>(
      this.env.counsellingPresentationList,
      ref => {

        let queryRef: any = ref;

        if (useCentreInQuery) {
          queryRef = queryRef.where('centreName', 'in', centreCodes);
        }

        if (this.start) {
          queryRef = queryRef
            .orderBy('startDate')
            .where(
              'startDate',
              '>=',
              firebase.firestore.Timestamp.fromDate(this.start)
            );
        }

        if (this.end) {
          if (!this.start) {
            queryRef = queryRef.orderBy('startDate');
          }

          queryRef = queryRef.where(
            'startDate',
            '<=',
            firebase.firestore.Timestamp.fromDate(this.end)
          );
        }

        if (!this.start && !this.end) {
          queryRef = queryRef.orderBy('createdAt', 'desc');
        }

        return queryRef;

      }
    ).valueChanges({ idField: 'id' });

    finalQuery$.pipe(takeUntil(this._destroyed$)).subscribe({

      next: (data) => {

        // Client-side centre filter for selections beyond Firestore's 10-value "in" limit
        if (centreCodes.length > 10) {
          data = data.filter((row: any) => centreSet.has(row.centreName));
        }

        this.dataSource.data = data;
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;

        this.loadingService.setLoading(false);

        this.snackBar.open(
          `Found ${data.length} presentations`,
          'Close',
          { duration: 2000 }
        );

      },

      error: (error) => {

        console.error(error);

        this.snackBar.open(
          'Error filtering presentations',
          'Close',
          { duration: 5000 }
        );

        this.loadingService.setLoading(false);

      }

    });

  }

  clearFilters(): void {
    const fy = this.financialYearService.getCurrentFinancialYear();
    this.start = fy.startDate;
    this.end = fy.endDate;
    this.filterCentre = [];
    this.searchText = '';
    this.loadPresentations();
    this.snackBar.open('Filters cleared', 'Close', { duration: 2000 });
  }

  formatDate(timestamp: firebase.firestore.Timestamp): string {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate();
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  formatDateRange(startDate: firebase.firestore.Timestamp, endDate: firebase.firestore.Timestamp): string {
    return `${this.formatDate(startDate)} - ${this.formatDate(endDate)}`;
  }

  viewPresentation(presentation: PresentationData): void {
    // Navigate to counselling-presentation with query params to load data
    this.router.navigate(['/home/reports/counselling-presentation'], {
      queryParams: { id: presentation.id }
    });
  }

  editPresentation(presentation: PresentationData): void {
    // Navigate to counselling-presentation with query params to edit
    this.router.navigate(['/home/reports/counselling-presentation'], {
      queryParams: { id: presentation.id, mode: 'edit' }
    });
  }

  deletePresentation(presentation: PresentationData): void {
    const confirmDelete = confirm(
      `Are you sure you want to delete the presentation for "${presentation.centreName}"?\nDate Range: ${this.formatDateRange(presentation.startDate, presentation.endDate)}`
    );

    if (confirmDelete) {
      this.loadingService.setLoading(true);
      this.firestore.collection(this.env.counsellingPresentationList)
        .doc(presentation.id)
        .delete()
        .then(() => {
          this.snackBar.open('Presentation deleted successfully', 'Close', { duration: 3000 });
          this.loadingService.setLoading(false);
        })
        .catch((error) => {
          console.error('Error deleting presentation:', error);
          this.snackBar.open('Error deleting presentation: ' + error.message, 'Close', { duration: 5000 });
          this.loadingService.setLoading(false);
        });
    }
  }

  duplicatePresentation(presentation: PresentationData): void {
    this.loadingService.setLoading(true);

    // Create a copy without the ID and update metadata
    const duplicateData = {
      ...presentation,
      createdAt: firebase.firestore.Timestamp.now(),
      createdBy: localStorage.getItem('userName') || 'Unknown',
      lastModified: firebase.firestore.Timestamp.now()
    };

    // Remove the id field
    delete duplicateData.id;

    this.firestore.collection(this.env.counsellingPresentationList)
      .add(duplicateData)
      .then((docRef) => {
        this.snackBar.open(`Presentation duplicated successfully! ID: ${docRef.id}`, 'Close', { duration: 5000 });
        this.loadingService.setLoading(false);
      })
      .catch((error) => {
        console.error('Error duplicating presentation:', error);
        this.snackBar.open('Error duplicating presentation: ' + error.message, 'Close', { duration: 5000 });
        this.loadingService.setLoading(false);
      });
  }

  exportToExcel(presentation: PresentationData): void {
    // TODO: Implement Excel export functionality
    this.snackBar.open('Excel export feature coming soon', 'Close', { duration: 3000 });
  }

  createNew(): void {
    this.router.navigate(['/home/reports/counselling-presentation']);
  }
}
